import React from 'react';
import { Link } from 'react-router-dom';
import { Accessibility, ArrowLeft, Mail, Phone, CheckCircle } from 'lucide-react';
import { siteConfig } from '../data/content';

const AccessibilityPage = () => {
  const lastUpdated = "December 30, 2024";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-teal-50 to-slate-50">
        <div className="container">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <Accessibility size={24} className="text-teal-700" />
            </div>
            <div>
              <h1 className="heading-1" data-testid="accessibility-title">Accessibility Statement</h1>
              <p className="text-sm text-slate-500">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-8">
              
              {/* Commitment */}
              <div>
                <h2 className="heading-3 mb-4">Our Commitment</h2>
                <p className="body-medium">
                  {siteConfig.name} is committed to ensuring digital accessibility for people with 
                  disabilities. We are continually improving the user experience for everyone 
                  and applying relevant accessibility standards.
                </p>
                <p className="body-medium mt-4">
                  We recognize that many of our patients, including stroke survivors, older adults, 
                  and individuals with neurological conditions, may have varying accessibility needs. 
                  We strive to make our website easy to navigate and understand for all visitors.
                </p>
              </div>

              {/* Standards */}
              <div>
                <h2 className="heading-3 mb-4">Accessibility Standards</h2>
                <p className="body-medium mb-4">
                  We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA. 
                  These guidelines explain how to make web content more accessible to people with 
                  a wide range of disabilities.
                </p>
              </div>

              {/* Features */}
              <div>
                <h2 className="heading-3 mb-4">Accessibility Features</h2>
                <p className="body-medium mb-4">
                  We have implemented the following accessibility features on our website:
                </p>
                <div className="grid gap-3">
                  {[
                    "Clear and consistent navigation throughout the site",
                    "Descriptive headings organized in a logical hierarchy",
                    "Sufficient color contrast for text readability",
                    "Readable font sizes (minimum 16px for body text)",
                    "Large touch targets for mobile users (minimum 44×44 pixels)",
                    "Keyboard navigation support for all interactive elements",
                    "Clear focus indicators for keyboard users",
                    "Descriptive link text that makes sense out of context",
                    "Form labels and error messages clearly associated with inputs",
                    "Alternative text for meaningful images",
                    "Consistent and predictable page layouts",
                    "Support for screen readers and assistive technologies"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <CheckCircle size={18} className="text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="body-small">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Assistive Technology */}
              <div>
                <h2 className="heading-3 mb-4">Assistive Technology</h2>
                <p className="body-medium">
                  Our website is designed to be compatible with assistive technologies including:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2 body-medium">
                  <li>Screen readers (JAWS, NVDA, VoiceOver)</li>
                  <li>Screen magnification software</li>
                  <li>Speech recognition software</li>
                  <li>Keyboard-only navigation</li>
                </ul>
              </div>

              {/* Browser Support */}
              <div>
                <h2 className="heading-3 mb-4">Browser Compatibility</h2>
                <p className="body-medium">
                  Our website is designed to work with the latest versions of major browsers 
                  including Chrome, Firefox, Safari, and Edge. We recommend using an up-to-date 
                  browser for the best experience.
                </p>
              </div>

              {/* Known Limitations */}
              <div>
                <h2 className="heading-3 mb-4">Known Limitations</h2>
                <p className="body-medium">
                  While we strive for full accessibility, we acknowledge there may be some 
                  limitations. We are actively working to identify and address any barriers. 
                  If you encounter accessibility issues, please let us know.
                </p>
              </div>

              {/* Feedback */}
              <div>
                <h2 className="heading-3 mb-4">Feedback</h2>
                <p className="body-medium">
                  We welcome your feedback on the accessibility of our website. If you encounter 
                  any barriers or have suggestions for improvement, please contact us. We take 
                  accessibility feedback seriously and will work to address issues promptly.
                </p>
              </div>

              {/* Alternative Access */}
              <div>
                <h2 className="heading-3 mb-4">Alternative Access</h2>
                <p className="body-medium">
                  If you are unable to access any content or use any feature on this website 
                  due to a disability, please contact us. We will work with you to provide the 
                  information or service through an alternative communication method.
                </p>
              </div>

              {/* Contact */}
              <div className="pt-6 border-t border-slate-200">
                <h2 className="heading-3 mb-4">Contact Us</h2>
                <p className="body-medium mb-4">
                  For accessibility questions, feedback, or assistance:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-teal-600" />
                    <a href={`tel:${siteConfig.phone}`} className="link">{siteConfig.phone}</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-teal-600" />
                    <a href={`mailto:${siteConfig.email}`} className="link">{siteConfig.email}</a>
                  </div>
                </div>
                <p className="body-small text-slate-500 mt-4">
                  We aim to respond to accessibility feedback within 2 business days.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccessibilityPage;
